import React from 'react';
import { Clock, CheckCircle } from 'lucide-react';
import { normalizeAddonOptions } from '../../../utils/addonOptions';

const PackageSelection = ({
  packages,
  selectedPackage,
  onSelectPackage,
  onChangePackage,
  selectedAddonIds = [],
  onToggleAddon,
  error
}) => {
  if (!selectedPackage) {
    return (
      <div>
        <h2 className="marketing-display-md mb-5 text-m-ink">
          Select your package
        </h2>
        <div className="space-y-3">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              type="button"
              className="w-full cursor-pointer rounded-2xl border border-m-border-light bg-m-surface p-5 text-left transition-colors hover:border-m-ink"
              onClick={() => onSelectPackage(pkg.id)}
            >
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                <div className="w-full flex-1 sm:w-auto">
                  <h3 className="mb-1 text-base font-medium text-m-ink">
                    {pkg.name}
                  </h3>
                  <p className="mb-2 text-sm text-m-ink-muted">
                    {pkg.description}
                  </p>
                  <div className="mb-2 flex items-center text-sm text-m-ink-muted">
                    <Clock className="mr-1.5 h-3.5 w-3.5" />
                    <span className="font-medium">{pkg.duration}</span>
                  </div>
                  {pkg.includes && pkg.includes.length > 0 && (
                    <ul className="space-y-1">
                      {pkg.includes.slice(0, 3).map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-sm text-m-ink-muted"
                        >
                          <CheckCircle className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 text-m-ink" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="w-full text-left sm:ml-4 sm:w-auto sm:text-right">
                  <div className="text-2xl font-medium tracking-tight text-m-ink">
                    ${Number(pkg.price || 0).toFixed(2)}
                  </div>
                  <div className="text-xs text-m-ink-faint">per event</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        {error && (
          <p className="mt-4 text-center text-sm font-medium text-red-600">
            {error}
          </p>
        )}
        {!error && (
          <p className="mt-4 text-center text-sm text-m-ink-muted">
            Select a package to continue
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="marketing-display-md text-m-ink">
          Selected package
        </h2>
        <button
          type="button"
          onClick={onChangePackage}
          className="text-sm text-m-ink-muted underline-offset-2 hover:text-m-ink hover:underline"
        >
          Change package
        </button>
      </div>
      <div className="rounded-2xl border border-m-ink bg-m-surface p-5 sm:p-6">
        <div className="mb-3 flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
          <div className="w-full flex-1 sm:w-auto">
            <h3 className="mb-1 text-base font-medium text-m-ink">
              {selectedPackage.name}
            </h3>
            <p className="mb-2 text-sm text-m-ink-muted">
              {selectedPackage.description}
            </p>
            <div className="mb-2 flex items-center text-sm text-m-ink-muted">
              <Clock className="mr-1.5 h-3.5 w-3.5 text-m-ink" />
              <span className="font-medium">{selectedPackage.duration}</span>
            </div>
            {selectedPackage.includes && selectedPackage.includes.length > 0 && (
              <div>
                <h4 className="mb-1.5 text-xs font-medium text-m-ink">
                  Included
                </h4>
                <ul className="space-y-1">
                  {selectedPackage.includes.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-m-ink-muted"
                    >
                      <CheckCircle className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 text-m-ink" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="w-full text-left sm:ml-4 sm:w-auto sm:text-right">
            <div className="text-2xl font-medium tracking-tight text-m-ink">
              ${Number(selectedPackage.price || 0).toFixed(2)}
            </div>
            <div className="text-xs text-m-ink-faint">per event</div>
          </div>
        </div>
        {normalizeAddonOptions(selectedPackage.addons).length > 0 ? (
          <div className="mt-4 border-t border-m-border-light pt-4">
            <h4 className="mb-2 text-xs font-medium text-m-ink">Optional add-ons</h4>
            <ul className="space-y-2">
              {normalizeAddonOptions(selectedPackage.addons).map((addon) => {
                const checked = selectedAddonIds.includes(addon.name);
                return (
                  <li key={addon.name}>
                    <label className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-m-border-light px-3 py-2 hover:border-m-ink">
                      <span className="flex min-w-0 items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          onChange={() => onToggleAddon?.(addon)}
                        />
                        <span>
                          <span className="block text-sm text-m-ink">{addon.name}</span>
                          {addon.description ? (
                            <span className="block text-xs text-m-ink-faint">{addon.description}</span>
                          ) : null}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-medium tabular-nums text-m-ink">
                        +${Number(addon.price || 0).toFixed(2)}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PackageSelection;
